import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useTheme } from '../components/theme-provider'
import { getPlatformLinks } from '../config/platformLinks'
import * as authService from '../services/authService'
import { IconEye, IconEyeOff } from '../components/icons'
import { Button } from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { StatusModal, useStatusModal } from '../components/ui/StatusModal'

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-muted-foreground">{label}</p>
      <p className="rounded-2xl border border-border bg-muted/30 px-4 py-2.5 text-sm text-foreground">
        {value || '—'}
      </p>
    </div>
  )
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
  autoComplete,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  visible: boolean
  onToggleVisible: () => void
  autoComplete: string
}) {
  return (
    <Input
      id={id}
      type={visible ? 'text' : 'password'}
      label={label}
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      minLength={id === 'newPassword' ? 8 : 1}
      endAdornment={
        <button
          type="button"
          className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onToggleVisible}
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? (
            <IconEyeOff className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          ) : (
            <IconEye className="h-[1.125rem] w-[1.125rem]" aria-hidden />
          )}
        </button>
      }
    />
  )
}

export default function Settings() {
  const { user, logout, loading: authLoading } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const platformLinks = getPlatformLinks()
  const { pushToast } = useToast()
  const { openError, statusModalProps } = useStatusModal()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [logoutBusy, setLogoutBusy] = useState(false)

  async function handleChangePassword(e) {
    e.preventDefault()
    setPasswordError(null)

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.')
      return
    }

    setPasswordSaving(true)
    try {
      await authService.changePassword({ currentPassword, newPassword })
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      pushToast({
        message: 'Password updated. Use your new password the next time you sign in.',
        variant: 'success',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not change password.'
      setPasswordError(message)
      openError({
        title: 'Password not changed',
        description: message,
      })
    } finally {
      setPasswordSaving(false)
    }
  }

  async function handleLogout() {
    setLogoutBusy(true)
    try {
      await logout()
      navigate('/login', { replace: true })
    } finally {
      setLogoutBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Card>
        <CardHeader
          title="Account"
          description="Your platform operator identity from the last sign-in."
        />
        <dl className="grid gap-4 sm:grid-cols-2">
          <ReadOnlyField label="Display name" value={user?.name || ''} />
          <ReadOnlyField label="Email" value={user?.email || ''} />
        </dl>
      </Card>

      <Card>
        <CardHeader
          title="Password"
          description="Change your super admin password."
        />
        <form className="space-y-4" onSubmit={(e) => void handleChangePassword(e)}>
          <PasswordField
            id="currentPassword"
            label="Current password"
            value={currentPassword}
            onChange={setCurrentPassword}
            visible={showCurrentPassword}
            onToggleVisible={() => setShowCurrentPassword((v) => !v)}
            autoComplete="current-password"
          />
          <PasswordField
            id="newPassword"
            label="New password"
            value={newPassword}
            onChange={setNewPassword}
            visible={showNewPassword}
            onToggleVisible={() => setShowNewPassword((v) => !v)}
            autoComplete="new-password"
          />
          <PasswordField
            id="confirmPassword"
            label="Confirm new password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            visible={showConfirmPassword}
            onToggleVisible={() => setShowConfirmPassword((v) => !v)}
            autoComplete="new-password"
          />

          {passwordError ? (
            <p className="text-sm text-destructive" role="alert">
              {passwordError}
            </p>
          ) : null}

          <Button type="submit" disabled={passwordSaving}>
            {passwordSaving ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Session"
          description="Sign out revokes your session on the server and clears saved login state in this browser."
        />
        <Button
          type="button"
          variant="secondary"
          disabled={authLoading || logoutBusy}
          onClick={() => void handleLogout()}
        >
          {logoutBusy ? 'Signing out…' : 'Sign out'}
        </Button>
      </Card>

      {platformLinks.length > 0 ? (
        <Card>
          <CardHeader
            title="Platform links"
            description="Shortcuts configured for this deployment."
          />
          <ul className="divide-y divide-border rounded-2xl border border-border">
            {platformLinks.map((link) => (
              <li key={link.href} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{link.label}</p>
                  {link.description ? (
                    <p className="mt-0.5 text-sm text-muted-foreground">{link.description}</p>
                  ) : null}
                </div>
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-sm font-medium text-primary hover:text-primary/80"
                >
                  Open →
                </a>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title="Appearance"
          description="Theme preference is saved in this browser and applies across the console."
        />
        <Select
          id="theme"
          label="Theme"
          value={theme ?? 'system'}
          onChange={(e) => setTheme(e.target.value)}
        >
          <option value="system">System</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </Select>
      </Card>

      <StatusModal {...statusModalProps} />
    </div>
  )
}
